import { Component, computed, effect, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { describeCron } from '../../utils/cron';

/**
 * Raw cron expression input with validation and a plain-English reading of what was typed.
 *
 * The control is owned by the caller, which should validate it with `cronExpressionValidator` and keep
 * it outside any autosaving form group: half-typed text is not a schedule, so saving per keystroke
 * would persist whatever transient expression happened to parse. {@link commit} fires when the user is
 * done with the field instead, on blur or Enter.
 */
@Component({
  selector: 'app-cron-field',
  imports: [ReactiveFormsModule, InputTextModule],
  templateUrl: './cron-field.component.html'
})
export class CronFieldComponent {
  readonly control = input.required<FormControl<string>>();
  readonly inputId = input.required<string>();
  readonly label = input('Schedule (cron expression)');

  /** Emitted when the user finishes with the field, on blur or Enter. */
  readonly commit = output<void>();

  readonly #value = signal('');
  protected readonly description = computed(() => describeCron(this.#value()));

  constructor() {
    effect(onCleanup => {
      const control = this.control();
      this.#value.set(control.value);

      const subscription = control.valueChanges.subscribe(value => this.#value.set(value));
      onCleanup(() => subscription.unsubscribe());
    });
  }
}
